/**
 * Card Notes Store (Zustand with localStorage persistence)
 *
 * Manages contextual notes attached to financial metric cards.
 * Notes are scoped by metric key + period key and visible only
 * to CEO, VP, and director roles.
 *
 * When the backend is ready, replace localStorage persistence
 * with API calls.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CardNote {
  id: string
  /** Metric key, e.g. "receitaBruta", "folha" */
  metric: string
  /** Period key, e.g. "2026-01-01_2026-03-31" (startDate_endDate) */
  periodKey: string
  authorId: string
  authorName: string
  content: string
  createdAt: string
  updatedAt?: string
}

interface CardNotesState {
  notes: CardNote[]
  addNote: (note: Omit<CardNote, 'id' | 'createdAt'>) => void
  updateNote: (id: string, content: string) => void
  deleteNote: (id: string) => void
  getNotesForCard: (metric: string, periodKey: string) => CardNote[]
}

/** Roles that can see and manage card notes */
export const NOTES_ALLOWED_ROLES = ['ceo', 'vice-presidente', 'diretor-executivo', 'diretor-tecnico', 'admin']

export const useCardNotesStore = create<CardNotesState>()(
  persist(
    (set, get) => ({
      notes: [],

      addNote: (note) =>
        set((state) => ({
          notes: [
            {
              ...note,
              id: crypto.randomUUID(),
              createdAt: new Date().toISOString(),
            },
            ...state.notes,
          ],
        })),

      updateNote: (id, content) =>
        set((state) => ({
          notes: state.notes.map((n) =>
            n.id === id
              ? { ...n, content, updatedAt: new Date().toISOString() }
              : n
          ),
        })),

      deleteNote: (id) =>
        set((state) => ({
          notes: state.notes.filter((n) => n.id !== id),
        })),

      getNotesForCard: (metric, periodKey) =>
        get().notes.filter(
          (n) => n.metric === metric && n.periodKey === periodKey
        ),
    }),
    {
      name: 'card-notes',
    }
  )
)
